import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationEnd, RouterEvent } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { User, UserToken } from '@model/authentication.model';

import { Logger } from '@service/logger.service';
import { AuthenticationService } from '@service/authentication.service';
import { PostMessageBridgeFactory, IPostMessageEventTarget } from './core/postmessage';

const log: Logger = new Logger('TA');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  initialized: boolean = false;

  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private router: Router,
    private translate: TranslateService,
    private authenticationService: AuthenticationService,
    @Inject(PostMessageBridgeFactory) private bridgeFactory: PostMessageBridgeFactory
  ) {}

  ngOnInit(): void {
    this.initTranslation();
    this.postMessage();
    this.onRouteChange();
    this.authenticationService.startWatching();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  initTranslation(): void {
    this.translate.use('en');
  }

  onRouteChange(): void {
    this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe((event: RouterEvent) => {
      this.scrollToTopOnNavigationEnd(event);
    });
  }

  private scrollToTopOnNavigationEnd(event: RouterEvent): void {
    if (!(event instanceof NavigationEnd)) {
      return;
    }
    window.scrollTo(0, 0);
  }

  private postMessage(): void {
    const parentWindow: IPostMessageEventTarget = window.parent;
    const currentWindow: IPostMessageEventTarget = window;

    this.bridgeFactory
      .makeInstance()
      .setEnableLogging(true)
      .connect(currentWindow, parentWindow)
      .makeBridge('ChildInit')
      .makeBridge('Token')
      .makeBridge('Unauthorized')
      .addListener('Token', (user: User) => {
        log.debug('Got credentials');
        this.authenticationService.sendUserMessage(user).then(() => {
          this.initialized = true;
        });
      });

    parentWindow.postMessage([{ channel: 'ChildInit', message: 'ta' }], '*');
  }
}
